import React, { useRef, useState } from "react";
import { FileText, Upload, Eye, CheckCircle2, Clock, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, API, getToken, formatApiError } from "@/lib/api";

export const docFileUrl = (id) => `${API}/verification/documents/${id}/file?auth=${encodeURIComponent(getToken() || "")}`;

const DOC_STATUS = {
  pending: { label: "Under review", cls: "bg-sky-50 text-sky-700 border-sky-200", icon: Clock },
  approved: { label: "Approved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  rejected: { label: "Rejected", cls: "bg-red-50 text-red-700 border-red-200", icon: AlertTriangle },
};

export function DocStatusPill({ status, testId }) {
  const s = DOC_STATUS[status];
  if (!s) return <span data-testid={testId} className="px-2.5 py-1 rounded-full text-[11px] font-semibold border bg-neutral-50 text-neutral-500 border-neutral-200">Not uploaded</span>;
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${s.cls}`}>
      <s.icon size={11} /> {s.label}
    </span>
  );
}

function DocRow({ item, uploadUrl, onChanged, readOnly }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const doc = item.document;

  async function upload(file) {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File is larger than 10 MB"); return; }
    const fd = new FormData();
    fd.append("doc_type", item.key);
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(uploadUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${item.label} uploaded`);
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  const canUpload = !readOnly && doc?.status !== "approved";
  return (
    <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`doc-row-${item.key}`}>
      <div className="w-10 h-10 rounded-xl bg-neutral-100 text-neutral-500 flex items-center justify-center flex-shrink-0"><FileText size={18} /></div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-neutral-900">
          {item.label} {item.required ? <span className="text-red-500">*</span> : <span className="text-[10px] font-medium text-neutral-400 uppercase tracking-wider ml-1">Optional</span>}
        </p>
        <p className="text-xs text-neutral-500 mt-0.5">{item.hint}</p>
        {doc && <p className="text-[11px] text-neutral-400 mt-1 truncate">{doc.original_filename} · {(doc.size / 1024).toFixed(0)} KB</p>}
        {doc?.status === "rejected" && doc.review_note && (
          <p data-testid={`doc-note-${item.key}`} className="text-xs text-red-700 bg-red-50 rounded-lg px-2.5 py-1.5 mt-2">Reviewer note: {doc.review_note}</p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <DocStatusPill status={doc?.status} testId={`doc-status-${item.key}`} />
        {doc && (
          <a href={docFileUrl(doc.id)} target="_blank" rel="noopener noreferrer" data-testid={`doc-view-${item.key}`} className="p-2 rounded-full hover:bg-neutral-100 text-neutral-600" aria-label="View document"><Eye size={15} /></a>
        )}
        {canUpload && (
          <>
            <input ref={inputRef} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" data-testid={`doc-input-${item.key}`} onChange={(e) => upload(e.target.files?.[0])} />
            <button type="button" disabled={busy} data-testid={`doc-upload-${item.key}`} onClick={() => inputRef.current?.click()} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold border border-neutral-200 bg-white hover:border-propera hover:text-propera transition-colors disabled:opacity-60">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              {doc ? "Replace" : "Upload"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function ProgressBar({ done, total, testId }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div data-testid={testId}>
      <div className="flex justify-between text-xs text-neutral-500 mb-1.5"><span>{done} of {total} mandatory documents</span><span className="tabular">{pct}%</span></div>
      <div className="h-2 rounded-full bg-neutral-100 overflow-hidden"><div className="h-full bg-propera transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export default function VerificationDocList({ items, uploadUrl, onChanged, readOnly = false }) {
  return (
    <div className="soft-card divide-y divide-neutral-100" data-testid="verification-doc-list">
      {items.map((it) => <DocRow key={it.key} item={it} uploadUrl={uploadUrl} onChanged={onChanged} readOnly={readOnly} />)}
    </div>
  );
}
